'use client';

import { c as _c } from "react/compiler-runtime";
import { useActiveWallet, useGetMarketChainMetadata } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserIdentity } from '@/components/profile/user-identity';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CheckboxField } from '@/components/ui/checkbox-field';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon, ArrowDown01IconSolid, ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { useOutcomesLimits } from '@/hooks/launchpad/use-outcomes-limits';
import { effectiveImageUrl, effectiveLabel, effectiveTicker, type SuggestionWire } from '../types';
interface CreatorReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketPubkey: string;
  suggestion: SuggestionWire | null;
  onConfirm: (suggestion: SuggestionWire) => void;
  isPending: boolean;
}
export function CreatorReviewModal(t0) {
  const $ = _c(121);
  const {
    open,
    onOpenChange,
    marketPubkey,
    suggestion,
    onConfirm,
    isPending
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.creator");
  const tWizard = useTranslations("launchpad.suggestedOutcomes.wizard");
  const tErrors = useTranslations("launchpad.errors");
  const [disclaimerAcked, setDisclaimerAcked] = useState(false);
  const {
    activeWallet
  } = useActiveWallet();
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        setDisclaimerAcked(false);
      }
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const {
    maxOutcomes
  } = useOutcomesLimits(marketPubkey);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["outcomeCount", "creator"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== marketPubkey) {
    t4 = {
      pubkey: marketPubkey,
      fields: t3
    };
    $[4] = marketPubkey;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const chainMetadata = useGetMarketChainMetadata(t4);
  const marketOutcomeCount = chainMetadata.data?.outcomeCount ?? null;
  const marketCreator = chainMetadata.data?.creator ?? null;
  const wouldExceedCap = maxOutcomes !== null && marketOutcomeCount !== null && marketOutcomeCount + 1 > maxOutcomes;
  const viewerIsCreator = marketCreator !== null && activeWallet?.address !== undefined && activeWallet.address === marketCreator;
  if (suggestion === null) {
    return null;
  }
  let t5;
  if ($[6] !== suggestion) {
    t5 = effectiveLabel(suggestion);
    $[6] = suggestion;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const label = t5;
  let t6;
  if ($[8] !== suggestion) {
    t6 = effectiveTicker(suggestion);
    $[8] = suggestion;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const ticker = t6;
  let t7;
  if ($[10] !== suggestion) {
    t7 = effectiveImageUrl(suggestion);
    $[10] = suggestion;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const imageUrl = t7;
  let t8;
  if ($[12] !== suggestion.suggesterPubkey) {
    t8 = suggestion.suggesterPubkey.slice(0, 4);
    $[12] = suggestion.suggesterPubkey;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== suggestion.suggesterPubkey) {
    t9 = suggestion.suggesterPubkey.slice(-4);
    $[14] = suggestion.suggesterPubkey;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const truncatedPubkey = `${t8}…${t9}`;
  const suggester = suggestion.suggester;
  let t10;
  if ($[16] !== suggester.connectedProviders) {
    t10 = suggester.connectedProviders ?? [];
    $[16] = suggester.connectedProviders;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const connectedProviders = t10;
  let t11;
  if ($[18] !== onOpenChange) {
    t11 = () => onOpenChange(false);
    $[18] = onOpenChange;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("cancel");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== isPending || $[23] !== t11 || $[24] !== t12) {
    t13 = <AnimatedButton type="button" variant="secondary" onClick={t11} disabled={isPending}>{t12}</AnimatedButton>;
    $[22] = isPending;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onConfirm || $[27] !== suggestion || $[28] !== viewerIsCreator) {
    t14 = () => {
      if (viewerIsCreator) {
        onConfirm(suggestion);
      }
    };
    $[26] = onConfirm;
    $[27] = suggestion;
    $[28] = viewerIsCreator;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = isPending || !disclaimerAcked || wouldExceedCap || !viewerIsCreator;
  let t16;
  if ($[30] !== isPending || $[31] !== t) {
    t16 = isPending ? t("adding") : t("addToMarket");
    $[30] = isPending;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isPending || $[34] !== t14 || $[35] !== t15 || $[36] !== t16) {
    t17 = <AnimatedButton type="button" variant="primary" onClick={t14} disabled={t15} loading={isPending}>{t16}</AnimatedButton>;
    $[33] = isPending;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t13 || $[39] !== t17) {
    t18 = <div className="flex items-center justify-between gap-3">{t13}{t17}</div>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  const footer = t18;
  let t19;
  if ($[41] !== t) {
    t19 = t("title");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== imageUrl || $[44] !== label) {
    t20 = <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-14" sizes="56px" rounded="rounded-item" className="size-14 shrink-0" imageClassName="size-14 object-cover" />;
    $[43] = imageUrl;
    $[44] = label;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== ticker) {
    t21 = <div className="font-mono text-body font-bold text-foreground-primary">${ticker}</div>;
    $[46] = ticker;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== label) {
    t22 = <div className="truncate text-caption text-foreground-secondary">{label}</div>;
    $[48] = label;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t21 || $[51] !== t22) {
    t23 = <div className="min-w-0">{t21}{t22}</div>;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t20 || $[54] !== t23) {
    t24 = <div className="flex items-center gap-3">{t20}{t23}</div>;
    $[53] = t20;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== suggestion.reason) {
    t25 = suggestion.reason !== null && suggestion.reason.length > 0 ? <p className="border-t pt-3 text-body text-foreground-primary">“{suggestion.reason}”</p> : null;
    $[56] = suggestion.reason;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t) {
    t26 = t("suggestedByLabel");
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t26) {
    t27 = <span className="shrink-0">{t26}</span>;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== connectedProviders || $[63] !== suggester) {
    t28 = {
      ...suggester,
      connectedProviders
    };
    $[62] = connectedProviders;
    $[63] = suggester;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28 || $[66] !== truncatedPubkey) {
    t29 = <UserIdentity user={t28} variant="with-avatar" size="sm" fallback={truncatedPubkey} />;
    $[65] = t28;
    $[66] = truncatedPubkey;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== t27 || $[69] !== t29) {
    t30 = <div className="flex min-w-0 items-center gap-2 text-body text-foreground-secondary">{t27}{t29}</div>;
    $[68] = t27;
    $[69] = t29;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Icon icon={ArrowUp01IconSolid} className="size-4 text-feedback-success-content" aria-hidden="true" />;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== suggestion.upvotes) {
    t32 = <span className="flex items-center gap-1">{t31}<span className="tabular-nums text-foreground-primary">{suggestion.upvotes}</span></span>;
    $[72] = suggestion.upvotes;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Icon icon={ArrowDown01IconSolid} className="size-4 text-feedback-error-content" aria-hidden="true" />;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== suggestion.downvotes) {
    t34 = <span className="flex items-center gap-1">{t33}<span className="tabular-nums text-foreground-primary">{suggestion.downvotes}</span></span>;
    $[75] = suggestion.downvotes;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t32 || $[78] !== t34) {
    t35 = <div className="flex shrink-0 items-center gap-2 text-body text-foreground-secondary">{t32}{t34}</div>;
    $[77] = t32;
    $[78] = t34;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t30 || $[81] !== t35) {
    t36 = <div className="flex items-center justify-between gap-3 border-t pt-3">{t30}{t35}</div>;
    $[80] = t30;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== t24 || $[84] !== t25 || $[85] !== t36) {
    t37 = <div className="flex flex-col gap-3 rounded-panel-row border bg-background-secondary p-4">{t24}{t25}{t36}</div>;
    $[83] = t24;
    $[84] = t25;
    $[85] = t36;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Icon icon={Alert01Icon} className="size-5 shrink-0 text-feedback-warning-content" aria-hidden="true" />;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== tWizard) {
    t39 = tWizard("disclaimerHeading");
    $[88] = tWizard;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t39) {
    t40 = <p className="text-body font-medium text-foreground-primary">{t39}</p>;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== tWizard) {
    t41 = tWizard("disclaimerBody");
    $[92] = tWizard;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== t41) {
    t42 = <p className="text-caption text-foreground-secondary">{t41}</p>;
    $[94] = t41;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== marketOutcomeCount || $[97] !== maxOutcomes || $[98] !== tErrors || $[99] !== tWizard || $[100] !== wouldExceedCap) {
    t43 = maxOutcomes !== null && marketOutcomeCount !== null ? <p className={wouldExceedCap ? "text-caption font-medium text-feedback-error-content" : "text-caption text-foreground-tertiary"}>{wouldExceedCap ? tErrors("marketAtCapacity", {
        max: maxOutcomes
      }) : tWizard("marketCapacity", {
        current: marketOutcomeCount,
        max: maxOutcomes
      })}</p> : null;
    $[96] = marketOutcomeCount;
    $[97] = maxOutcomes;
    $[98] = tErrors;
    $[99] = tWizard;
    $[100] = wouldExceedCap;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t40 || $[103] !== t42 || $[104] !== t43) {
    t44 = <div className="flex items-start gap-3 rounded-panel-row border border-feedback-warning-border/40 bg-feedback-warning-accent/10 p-4">{t38}<div className="flex flex-col gap-1">{t40}{t42}{t43}</div></div>;
    $[102] = t40;
    $[103] = t42;
    $[104] = t43;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  let t45;
  if ($[106] !== tWizard) {
    t45 = tWizard("disclaimerAcknowledge");
    $[106] = tWizard;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] !== disclaimerAcked || $[109] !== t45) {
    t46 = <CheckboxField label={t45} checked={disclaimerAcked} onCheckedChange={setDisclaimerAcked} required={true} />;
    $[108] = disclaimerAcked;
    $[109] = t45;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== t37 || $[112] !== t44 || $[113] !== t46) {
    t47 = <div className="flex flex-col gap-4">{t37}{t44}{t46}</div>;
    $[111] = t37;
    $[112] = t44;
    $[113] = t46;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== footer || $[116] !== onOpenChange || $[117] !== open || $[118] !== t19 || $[119] !== t47) {
    t48 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" size="default" title={t19} footer={footer} disableDesktopSwipe={true} contentClassName="pt-3 sm:pt-3">{t47}</SilkSheet>;
    $[115] = footer;
    $[116] = onOpenChange;
    $[117] = open;
    $[118] = t19;
    $[119] = t47;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  return t48;
}